// SettingsPanel.jsx - Complete Settings Panel with all features
// Baba AI - Full settings restored from Electron version
// Updated: November 26, 2025

import React, { useState, useEffect } from 'react';
import './SettingsPanel.css';
import googleDriveService from '../services/googleDriveService';
import { runAllChecks } from '../services/systemChecks';

function SettingsPanel({ 
  onClose, 
  autoRunCheck = false,
  settings = {},
  onSettingsChange = () => {}
}) {
  // Google Drive state
  const [isDriveConnected, setIsDriveConnected] = useState(
    googleDriveService.isAuthenticated()
  );
  const [isInitializing, setIsInitializing] = useState(false);
  
  // System check state
  const [isRunningCheck, setIsRunningCheck] = useState(false);
  const [checkResults, setCheckResults] = useState(null);
  const [showResults, setShowResults] = useState(false);
  
  // Settings state (with defaults)
  const [localSettings, setLocalSettings] = useState({
    model: settings.model || 'meta-llama/Meta-Llama-3.1-405B-Instruct-Turbo',
    apiKey: settings.apiKey || localStorage.getItem('together_api_key') || '',
    theme: settings.theme || localStorage.getItem('baba_theme') || 'dark',
    fontSize: settings.fontSize || localStorage.getItem('baba_font_size') || 'medium',
    enableStreaming: settings.enableStreaming ?? localStorage.getItem('baba_streaming') !== 'false',
    autoScroll: settings.autoScroll ?? localStorage.getItem('baba_autoscroll') !== 'false',
    ...settings
  });

  // Workspace state
  const [workspaceInfo, setWorkspaceInfo] = useState(null);
  const [isLoadingWorkspace, setIsLoadingWorkspace] = useState(false);

  // Auto-run check if requested
  useEffect(() => {
    if (autoRunCheck) {
      runSystemCheck();
    }
  }, [autoRunCheck]);

  // Load workspace info on mount
  useEffect(() => {
    if (isDriveConnected) {
      loadWorkspaceInfo();
    }
  }, [isDriveConnected]);

  // ==========================================
  // SETTINGS HANDLERS
  // ==========================================

  const updateSetting = (key, value) => {
    const newSettings = { ...localSettings, [key]: value };
    setLocalSettings(newSettings);
    
    // Persist to localStorage
    switch(key) {
      case 'theme':
        localStorage.setItem('baba_theme', value);
        document.documentElement.setAttribute('data-theme', value);
        break;
      case 'fontSize':
        localStorage.setItem('baba_font_size', value);
        document.documentElement.style.fontSize = getFontSizeValue(value);
        break;
      case 'enableStreaming':
        localStorage.setItem('baba_streaming', value.toString());
        break;
      case 'autoScroll':
        localStorage.setItem('baba_autoscroll', value.toString());
        break;
      case 'apiKey':
        localStorage.setItem('together_api_key', value);
        break;
      default:
        break;
    }
    
    // Notify parent
    onSettingsChange(newSettings);
  };

  const getFontSizeValue = (size) => {
    switch(size) {
      case 'small': return '14px';
      case 'medium': return '16px';
      case 'large': return '18px';
      default: return '16px';
    }
  };

  // ==========================================
  // GOOGLE DRIVE HANDLERS
  // ==========================================

  const handleConnectDrive = () => {
    googleDriveService.initiateAuth();
  };

  const handleDisconnectDrive = () => {
    if (window.confirm('Disconnect Google Drive? You will need to reconnect to use cloud features.')) {
      googleDriveService.logout();
      setIsDriveConnected(false);
      setWorkspaceInfo(null);
    }
  };

  const handleInitializeWorkspace = async () => {
    setIsInitializing(true);
    try {
      await googleDriveService.initializeBabaWorkspace();
      await loadWorkspaceInfo();
      alert('Baba_Workspace folder structure created successfully!');
    } catch (error) {
      alert(`Failed to initialize workspace: ${error.message}`);
    } finally {
      setIsInitializing(false);
    }
  };

  const loadWorkspaceInfo = async () => {
    setIsLoadingWorkspace(true);
    try {
      const workspaceId = localStorage.getItem('baba_workspace_id');
      if (workspaceId) {
        setWorkspaceInfo({
          id: workspaceId,
          name: localStorage.getItem('baba_workspace_name') || 'Baba_Workspace'
        });
      }
    } catch (error) {
      console.error('Failed to load workspace info:', error);
    } finally {
      setIsLoadingWorkspace(false);
    }
  };

  const openWorkspaceInDrive = () => {
    const workspaceId = localStorage.getItem('baba_workspace_id');
    if (workspaceId) {
      window.open(`https://drive.google.com/drive/folders/${workspaceId}`, '_blank');
    }
  };

  // ==========================================
  // SYSTEM CHECK
  // ==========================================

  const runSystemCheck = async () => {
    setIsRunningCheck(true);
    setShowResults(true);
    
    try {
      const results = await runAllChecks();
      setCheckResults(results);
    } catch (error) {
      console.error('System check failed:', error);
      setCheckResults({
        passed: 0,
        failed: 1,
        total: 1,
        checks: [{ category: 'Error', name: 'System Check', passed: false, message: error.message }]
      });
    } finally {
      setIsRunningCheck(false);
    }
  };

  const getGroupedChecks = () => {
    if (!checkResults?.checks) return {};
    
    return checkResults.checks.reduce((groups, check) => {
      const category = check.category || 'Other';
      if (!groups[category]) groups[category] = [];
      groups[category].push(check);
      return groups;
    }, {});
  };

  // ==========================================
  // DATA MANAGEMENT
  // ==========================================

  const handleBackup = async () => {
    try {
      const backupData = {
        version: '1.0',
        timestamp: new Date().toISOString(),
        chats: JSON.parse(localStorage.getItem('baba_chats') || '[]'),
        settings: localSettings,
        userProfile: JSON.parse(localStorage.getItem('baba-user-profile') || '{}'),
        timePatterns: JSON.parse(localStorage.getItem('baba-time-patterns') || '{}'),
        stateDetection: JSON.parse(localStorage.getItem('baba-state-detection') || '{}')
      };
      
      const blob = new Blob([JSON.stringify(backupData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `baba_backup_${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      alert(`Backup failed: ${error.message}`);
    }
  };

  const handleRestore = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      
      try {
        const text = await file.text();
        const backupData = JSON.parse(text);
        
        if (!backupData.version) {
          throw new Error('Invalid backup file');
        }
        
        if (!window.confirm('This will replace all current data. Continue?')) {
          return;
        }
        
        // Restore data
        if (backupData.chats) {
          localStorage.setItem('baba_chats', JSON.stringify(backupData.chats));
        }
        if (backupData.settings) {
          Object.entries(backupData.settings).forEach(([key, value]) => {
            updateSetting(key, value);
          });
        }
        if (backupData.userProfile) {
          localStorage.setItem('baba-user-profile', JSON.stringify(backupData.userProfile));
        }
        if (backupData.timePatterns) {
          localStorage.setItem('baba-time-patterns', JSON.stringify(backupData.timePatterns));
        }
        if (backupData.stateDetection) {
          localStorage.setItem('baba-state-detection', JSON.stringify(backupData.stateDetection));
        }
        
        alert('Backup restored successfully! Refreshing...');
        window.location.reload();
      } catch (error) {
        alert(`Restore failed: ${error.message}`);
      }
    };
    input.click();
  };

  const handleClearChats = () => {
    if (window.confirm('Clear all chats? This cannot be undone!')) {
      localStorage.removeItem('baba_chats');
      localStorage.removeItem('baba_current_chat');
      alert('All chats cleared. Refreshing...');
      window.location.reload();
    }
  };

  const handleClearAllData = () => {
    if (window.confirm('Clear ALL local data? This will delete everything and cannot be undone!')) {
      localStorage.clear();
      alert('All data cleared. Refreshing...');
      window.location.reload();
    }
  };

  // ==========================================
  // RENDER
  // ==========================================

  return (
    <div className="settings-overlay" onClick={onClose}>
      <div className="settings-panel" onClick={(e) => e.stopPropagation()}>
        <div className="settings-header">
          <h2>⚙️ Settings</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        
        <div className="settings-content">
          {/* Model Selection */}
          <div className="settings-section">
            <h3>Model</h3>
            <div className="model-display">
              {localSettings.model}
            </div>
          </div>

          {/* API Key */}
          <div className="settings-section">
            <h3>API Key</h3>
            <input
              type="password"
              className="settings-input"
              value={localSettings.apiKey}
              onChange={(e) => updateSetting('apiKey', e.target.value)}
              placeholder="Together AI API Key"
            />
          </div>

          {/* Google Drive */}
          <div className="settings-section">
            <h3>Google Drive</h3>
            {isDriveConnected ? (
              <>
                <div className="status connected">
                  <span className="status-icon">✅</span>
                  <span>Connected to Google Drive</span>
                </div>
                {workspaceInfo && (
                  <div className="workspace-info">
                    <span>📁 {workspaceInfo.name}</span>
                    <button 
                      className="action-button small"
                      onClick={openWorkspaceInDrive}
                      title="Open in Google Drive"
                    >
                      🔗 Open
                    </button>
                  </div>
                )}
                <div className="button-row">
                  <button 
                    className="action-button"
                    onClick={handleInitializeWorkspace}
                    disabled={isInitializing}
                  >
                    {isInitializing ? '⏳ Creating...' : '📁 Initialize Workspace'}
                  </button>
                  <button 
                    className="action-button danger"
                    onClick={handleDisconnectDrive}
                  >
                    🔌 Disconnect
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="status disconnected">
                  <span className="status-icon">❌</span>
                  <span>Not connected</span>
                </div>
                <button 
                  className="action-button primary"
                  onClick={handleConnectDrive}
                >
                  🔗 Connect Google Drive
                </button>
              </>
            )}
          </div>

          {/* Theme */}
          <div className="settings-section">
            <h3>Theme</h3>
            <select 
              className="settings-select"
              value={localSettings.theme}
              onChange={(e) => updateSetting('theme', e.target.value)}
            >
              <option value="dark">🌙 Dark</option>
              <option value="light">☀️ Light</option>
              <option value="system">💻 System</option>
            </select>
          </div>

          {/* Font Size */}
          <div className="settings-section">
            <h3>Font Size</h3>
            <select 
              className="settings-select"
              value={localSettings.fontSize}
              onChange={(e) => updateSetting('fontSize', e.target.value)}
            >
              <option value="small">Small (14px)</option>
              <option value="medium">Medium (16px)</option>
              <option value="large">Large (18px)</option>
            </select>
          </div>

          {/* Toggles */}
          <div className="settings-section">
            <label className="toggle-row">
              <input
                type="checkbox"
                checked={localSettings.enableStreaming}
                onChange={(e) => updateSetting('enableStreaming', e.target.checked)}
              />
              <span className="toggle-label">
                <strong>Enable Response Streaming</strong>
                <small>See AI responses appear word-by-word</small>
              </span>
            </label>
            
            <label className="toggle-row">
              <input
                type="checkbox"
                checked={localSettings.autoScroll}
                onChange={(e) => updateSetting('autoScroll', e.target.checked)}
              />
              <span className="toggle-label">
                <strong>Auto-scroll to new messages</strong>
                <small>Automatically scroll when new messages arrive</small>
              </span>
            </label>
          </div>

          {/* Data Management */}
          <div className="settings-section">
            <h3>📦 Data Management</h3>
            <div className="button-stack">
              <button 
                className="action-button primary"
                onClick={handleBackup}
              >
                💾 Backup All Data
              </button>
              <button 
                className="action-button"
                onClick={handleRestore}
              >
                📥 Restore from Backup
              </button>
              <button 
                className="action-button warning"
                onClick={handleClearChats}
              >
                🗑️ Clear All Chats
              </button>
              <button 
                className="action-button danger"
                onClick={handleClearAllData}
              >
                ⚠️ Clear ALL Local Data
              </button>
            </div>
            <p className="help-text warning">
              Clearing data cannot be undone. Consider backing up first.
            </p>
          </div>

          {/* System Check */}
          <div className="settings-section">
            <h3>🔍 System Check</h3>
            <button 
              className="action-button primary full-width"
              onClick={runSystemCheck}
              disabled={isRunningCheck}
            >
              {isRunningCheck ? '⏳ Running...' : '🔧 Run System Check'}
            </button>
            <p className="help-text">Verify all features and integrations are working correctly</p>
            
            {showResults && checkResults && (
              <div className="system-check-results">
                <div className="results-summary">
                  <div className="summary-stats">
                    <span className="stat total">Total: {checkResults.total}</span>
                    <span className="stat passed">✅ {checkResults.passed}</span>
                    <span className="stat failed">❌ {checkResults.failed}</span>
                  </div>
                  <div className="summary-score">
                    {Math.round((checkResults.passed / checkResults.total) * 100)}%
                  </div>
                </div>
                
                {Object.entries(getGroupedChecks()).map(([category, checks]) => (
                  <div key={category} className="check-category">
                    <h4 className="category-title">{category}</h4>
                    {checks.map((check, index) => (
                      <div 
                        key={index} 
                        className={`check-item ${check.passed ? 'passed' : 'failed'}`}
                      >
                        <span className="check-icon">{check.passed ? '✅' : '❌'}</span>
                        <span className="check-name">{check.name}</span>
                        {check.message && (
                          <span className="check-message">{check.message}</span>
                        )}
                      </div>
                    ))}
                  </div>
                ))}
                
                <button 
                  className="action-button small"
                  onClick={() => setShowResults(false)}
                >
                  Close Results
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default SettingsPanel;
